import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useConvexAuth } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../backend/convex/_generated/dataModel';
import { Header } from '../components/Header';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { AssignedAssessments } from '../components/AssignedAssessments';
import { useWorkspace } from '../hooks/useWorkspace';
import '../App.css';

export const Dashboard: React.FC = () => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const { isLoading: isAuthLoading } = useConvexAuth();
  const { workspace } = useWorkspace(selectedWorkspaceId || undefined);
  
  const problems = useQuery(api.problems.list, 
    isAuthLoading ? "skip" : (selectedWorkspaceId ? { workspaceId: selectedWorkspaceId as any } : {})
  );
  const problemsLoading = problems === undefined || isAuthLoading;
  
  const { user, isLoaded: userLoaded } = useUser();
  const submissions = useQuery(api.submissions.listUserSubmissions, isAuthLoading ? "skip" : undefined);
  const getWorkspaces = useQuery(api.workspaces.listMyWorkspaces, isAuthLoading ? "skip" : undefined);
  const assignments = useQuery(api.assignments.listMyAssignments, isAuthLoading ? "skip" : undefined);

  // Check feature flags
  const submissionsEnabled = workspace?.features?.submissions !== false;
  const assessmentsEnabled = workspace?.features?.assessments !== false;
  const assignmentsEnabled = workspace?.features?.assignments !== false;

  useEffect(() => {
    document.title = 'LibreRank - Dashboard';
  }, []);

  // Set default workspace initially if available
  useEffect(() => {
    if (getWorkspaces && !selectedWorkspaceId) {
      const defaultWs = getWorkspaces.find((ws: Doc<"workspaces"> & { role: string }) => ws.slug === 'default');
      if (defaultWs) {
        setSelectedWorkspaceId(defaultWs._id);
      }
    }
  }, [getWorkspaces, selectedWorkspaceId]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'var(--success)';
      case 'medium': return 'var(--warning)';
      case 'hard': return 'var(--destructive)';
      default: return 'var(--foreground)';
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="app-container">
      <Header workspaceId={selectedWorkspaceId || undefined}>
        <WorkspaceSwitcher 
          selectedWorkspaceId={selectedWorkspaceId} 
          onSelect={setSelectedWorkspaceId} 
        />
      </Header>
      
      <main className="dashboard-content" style={{ padding: '2rem', overflowY: 'auto' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {userLoaded && user && assessmentsEnabled && assignmentsEnabled && (
            <AssignedAssessments 
              assignments={assignments} 
              formatDate={formatDate} 
            />
          )}

          <section className="practice-section" style={{ marginBottom: '3rem' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.8rem' }}>Practice Problems</h2>
            {problemsLoading ? (
              <p>Loading problems...</p>
            ) : !problems || problems.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '8px', color: 'var(--muted-foreground)' }}>
                No problems found in this workspace.
              </div>
            ) : (
              <div className="problems-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '1.5rem' 
              }}>
                {problems.map((problem: Doc<"problems">) => (
                  <Link 
                    key={problem.id} 
                    to={`/problems/${problem.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="problem-card" style={{ 
                      padding: '1.5rem', 
                      border: '1px solid var(--border)', 
                      borderRadius: '8px',
                      background: 'var(--card)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{problem.title}</h3>
                        <span style={{ 
                          fontSize: '0.8rem', 
                          fontWeight: 'bold', 
                          color: getDifficultyColor(problem.difficulty),
                          textTransform: 'capitalize'
                        }}>
                          {problem.difficulty}
                        </span>
                      </div>
                      <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem', margin: 0 }}>
                        Practice your coding skills with this {problem.difficulty} problem.
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {userLoaded && user && submissionsEnabled && (
            <section className="submissions-section">
              <h2 style={{ marginBottom: '1.5rem', fontSize: '1.8rem' }}>Your Submissions</h2>
              {!submissions ? (
                <p>Loading submissions...</p>
              ) : submissions.length === 0 ? (
                <p style={{ color: 'var(--muted-foreground)' }}>You haven't submitted any solutions yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '1rem 0.5rem' }}>Problem</th>
                        <th style={{ padding: '1rem 0.5rem' }}>Language</th>
                        <th style={{ padding: '1rem 0.5rem' }}>Status</th>
                        <th style={{ padding: '1rem 0.5rem' }}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((sub: Doc<"submissions">) => (
                        <tr key={sub._id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '1rem 0.5rem' }}>
                            <Link to={`/problems/${sub.problemId}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                              {sub.problemId}
                            </Link>
                          </td>
                          <td style={{ padding: '1rem 0.5rem', textTransform: 'capitalize' }}>{sub.language}</td>
                          <td style={{ padding: '1rem 0.5rem' }}>
                            <span style={{ 
                              padding: '0.2rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.8rem',
                              background: 
                                sub.status === 'graded' ? 'var(--success-muted)' : 
                                sub.status === 'grading' ? 'var(--info-muted)' : 
                                'var(--warning-muted)',
                              color: 
                                sub.status === 'graded' ? 'var(--success)' : 
                                sub.status === 'grading' ? 'var(--info)' : 
                                'var(--warning)',
                              border: '1px solid currentColor'
                            }}>
                              {sub.status}
                            </span>
                          </td>
                          <td style={{ padding: '1rem 0.5rem', color: 'var(--muted-foreground)', fontSize: '0.9rem' }}>
                            {formatDate(sub.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
};
